import React from "react";

import { EModuleType } from "../../Model/EModuleType";
import {
  EChillerControlStatus,
  EChillerRecoveryStatus,
  EChillerState,
  ModuleState,
} from "../../Model/ModuleState";
import { ETemperatureSensorType } from "../../Model/ETemperatureSensorType";

import { selectDetailModuleStateByType } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import {
  getModuleTemperatureChanged,
  getModuleTemperatureSensors,
} from "../../redux/reducers/TemperatureReducer";
import { getModules } from "../../redux/reducers/ModuleReducer";

import OverviewStatus, { EOverviewModuleStatus } from "./OverviewStatus";
import { ETemperatureState } from "../../Model/ETemperatureState";
import moment from "moment";
import { ModuleTemperatureChange } from "../../Model/Temperature";

const getStatusText = (state: EChillerState) => {
  switch (state) {
    case EChillerState.Ready:
      return "Normal";
    case EChillerState.Warning:
      return "Warning";
    default:
      return "Failed";
  }
};

const getModuleStatus = (state: EChillerState) => {
  switch (state) {
    case EChillerState.Ready:
      return EOverviewModuleStatus.Idle;
    case EChillerState.Warning:
      return EOverviewModuleStatus.Busy;
    default:
      return EOverviewModuleStatus.Error;
  }
};

const getTempStatus = (
  chillerState: ModuleState,
  moduleTemperatureChange: ModuleTemperatureChange
) => {
  let status = EChillerState.Ready;
  if (chillerState?.chiller) {
    const { state } = chillerState.chiller;
    status = state;
  }
  if (moduleTemperatureChange.state === ETemperatureState.Violation)
    status = EChillerState.Error;
  return status;
};

export default function TemperatureStatus() {
  const chamberModule = useTypedSelector((state) =>
    getModules(state, EModuleType.CHAMBER)
  )[0];
  const chillerState = useTypedSelector((state) =>
    selectDetailModuleStateByType(state, EModuleType.CHILLER)
  );
  const moduleTemperatureChange = useTypedSelector((state) =>
    getModuleTemperatureChanged(state, chamberModule.identifier)
  );

  // Get average and target temperature sensor name
  const sensors = useTypedSelector((state) =>
    getModuleTemperatureSensors(state, chamberModule.identifier)
  );
  const targetSensor = sensors?.temperatureSensors.find(
    (ts) => ts.type === ETemperatureSensorType.Target
  )!;
  const averageSensor = sensors?.temperatureSensors.find(
    (ts) => ts.type === ETemperatureSensorType.Average
  )!;

  // getting data for content

  const typeText = chillerState ? "Chiller" : "Peltier";
  const temperatureText =
    moduleTemperatureChange && averageSensor
      ? moduleTemperatureChange.data[averageSensor.name].toFixed(1) + "\u2103"
      : "...";
  const statusText = getStatusText(
    getTempStatus(chillerState, moduleTemperatureChange)
  );
  const moduleStatus = getModuleStatus(
    getTempStatus(chillerState, moduleTemperatureChange)
  );

  const statusses = {
    Type: typeText,
    Temperature: temperatureText,
    Status: statusText,
  };

  const errorMessages: string[] = [];
  if (moduleTemperatureChange.state === ETemperatureState.Violation) {
    errorMessages.push(
      `Temp Alert : Target ${moduleTemperatureChange.data[
        targetSensor.name
      ].toFixed(1)}°C at ${moment(moduleTemperatureChange.time).format(
        "YYYY-MM-DD hh:mm A"
      )}`
    );
  }
  if (
    chillerState?.chiller?.state === EChillerState.Error ||
    chillerState?.chiller?.state === EChillerState.Warning
  ) {
    chillerState.chiller.chillerControlState.forEach((control) => {
      const errorListMessage = [];
      if (control.connectionStatus === EChillerControlStatus.Disconnected) {
        errorListMessage.push("Disconnected");
      }
      if (control.sensorErrorList.length) {
        errorListMessage.push(control.sensorErrorList.join(", "));
      }
      if (control.recoveryStatus === EChillerRecoveryStatus.Recovery) {
        errorListMessage.push("In Recovery state mode");
      }

      if (errorListMessage.length)
        errorMessages.push(
          `Chiller ${control.name}: ${errorListMessage.join(", ")}`
        );
    });
  }

  return (
    <OverviewStatus
      name="Temperature"
      statusses={statusses}
      moduleStatus={moduleStatus}
      moduleType={EModuleType.CHILLER}
    >
      {errorMessages?.map((message) => {
        return (
          <span key={message} style={{ textAlign: "left", color: "red" }}>
            {message}
          </span>
        );
      })}
    </OverviewStatus>
  );
}
